import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableFooter from "@mui/material/TableFooter"
import Paper from "@mui/material/Paper"
import Pagination from "@mui/material/Pagination"
import PaginationItem from "@mui/material/PaginationItem"
import PatientDetailsTableHeadCellCard from "./PatientDetailsTableHeadCellCard"
import ArrowLeftIcon from "@assets/icons/arrowLeft"
import ArrowRightIcon from "@assets/icons/arrowRight"
import TableRightSideBarCell from "./TableRightSideBarCell"
import styles from "./PatientDetailsTable.module.css"
import { useSelector } from "react-redux"
import formatDateFromISOString from "src/utils/formatDate"
import usePatient from "@hooks/usePatient"
import { useEffect } from "react"
import { Loader } from "@components/Loader"
import { Link } from "react-router-dom"
import useDebounce from "@hooks/useDebounce"

const PatientDetailsTable = ({
  setShowFilters,
  setFilterToBeExpanded,
  setPaginationState,
  paginationState,
}) => {
  const { patients, isGetPatientLoading } = useSelector(
    (state) => state.patient
  )
  const { ownDetails } = useSelector((state) => state.profile)
  const { isEnabled } = ownDetails?.access?.visionPostpaid || {}
  const { handleGetPatient } = usePatient()
  const filterChangeDebounced = useDebounce(handleGetPatient, 1000)

  useEffect(() => {
    filterChangeDebounced(paginationState)
  }, [paginationState, handleGetPatient])

  const tableHeadCellStyles = {
    color: "var(--bg-theme-primary)",
    fontWeight: "700",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "12px 24px 12px 24px",
    height: "42px",
    backgroundColor: "var(--bg-blue-light)",
  }

  const tableBodyCellStyles = {
    color: "var(--quaternary-black)",
    fontWeight: "400",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "0px",
    height: "42px",
  }
  return (
    <div className={styles.tableAndFooterWrapper}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "0px",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          position: "relative",
          flexGrow: "1",

          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {},
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--border-theme-300)",
            borderRadius: "8px",
          },
          boxShadow: "none",
          "@media (max-width:700px)": {
            borderRadius: "16px",
            "&::-webkit-scrollbar": {
              width: "0px",
              height: "0px",
            },
          },
        }}
      >
        {isGetPatientLoading ? (
          <div className="absolute top-0 bottom-0 right-0 left-0 flex items-center justify-center bg-[--pure-white] z-50">
            <Loader />
          </div>
        ) : null}
        <div className={styles.tableAndBarWrapper}>
          <Table stickyHeader aria-label="simple table">
            <TableHead
              sx={{
                backgroundColor: "var(--bg-blue-light)",
              }}
            >
              <TableRow>
                <TableCell sx={tableHeadCellStyles}>
                  <PatientDetailsTableHeadCellCard
                    name="First Name"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    setPaginationState={setPaginationState}
                    sortPath="local.firstName"
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Last Name"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    setPaginationState={setPaginationState}
                    sortPath="local.lastName"
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Email"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    setPaginationState={setPaginationState}
                    sortPath="local.email"
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="State"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    setPaginationState={setPaginationState}
                    sortPath="local.province"
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Contact"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    setPaginationState={setPaginationState}
                    sortPath="local.contact"
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Pincode"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    setPaginationState={setPaginationState}
                    sortPath="local.pincode"
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Created At"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    setPaginationState={setPaginationState}
                    sortPath="created_at"
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Plan"
                    setShowFilters={setShowFilters}
                    setFilterToBeExpanded={setFilterToBeExpanded}
                    sortPath="plan"
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients?.data?.map((item) => {
                const {
                  firstName,
                  lastName,
                  email,
                  province,
                  contact,
                  pincode,
                } = item?.local || {}
                const { created_at, plan } = item

                return (
                  <TableRow
                    key={item._id}
                    sx={{
                      height: "42px",
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={tableBodyCellStyles}
                    >
                      <div className={styles.tableCellContent}>{firstName}</div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>{lastName}</div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>{email}</div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>{province}</div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>{contact}</div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>{pincode}</div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>
                        {formatDateFromISOString(created_at).date}
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      {plan === "active" ? (
                        <div className={styles.planActive}>
                          <div className={styles.planActiveDot}></div>
                          Active
                        </div>
                      ) : (
                        <Link
                          to={`/subscribe?email=${email}&name=${firstName}&isEnabled=${isEnabled}`}
                          className={styles.planInactive}
                        >
                          Activate
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <div className={styles.tableRightSideBar}>
            <div className={styles.rightSideBarHeadCell}></div>

            {patients?.data?.map((patient) => {
              return <TableRightSideBarCell patient={patient} />
            })}
          </div>
        </div>
      </TableContainer>

      <TableFooter
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid var(--border-theme-300)",
          backgroundColor: "white",
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px",
          padding: "5px",

          "@media (max-width:850px)": {
            flexDirection: "column",
            gap: "10px",
            width: "100%",
          },
          "@media (max-width:700px)": {
            backgroundColor: "transparent",
            border: "none",
          },
        }}
      >
        <div className={styles.rowsPerPageSection}>
          <h3>Rows per page</h3>
          <select
            disabled={isGetPatientLoading ? true : false}
            className={styles.rowsPerPageOptionsWrapper}
            onChange={(event) => {
              const value = event.currentTarget.value
              setPaginationState((previous) => {
                return { ...previous, page: 1, limit: value }
              })
            }}
            value={paginationState.limit}
          >
            <option value="10">1-10</option>
            <option value="20">1-20</option>
            <option value="30">1-30</option>
          </select>
          <h3>of patient details</h3>
        </div>

        <div className={styles.mobilePaginationSection}>
          <button
            disabled={isGetPatientLoading ? true : false}
            className={styles.mobilePaginationPreviousButton}
            onClick={() => {
              if (parseInt(paginationState.page) - 1 >= 1) {
                setPaginationState((previous) => {
                  return {
                    ...previous,
                    page: parseInt(previous.page) - 1,
                  }
                })
              }
            }}
          >
            <ArrowLeftIcon stroke="var(--pure-white)" />
            Previous
          </button>
          <select
            disabled={isGetPatientLoading ? true : false}
            className={styles.rowsPerPageOptionsWrapper}
            onChange={(event) => {
              const value = event.currentTarget.value
              setPaginationState((previous) => {
                return { ...previous, page: 1, limit: value }
              })
            }}
            value={paginationState.limit}
          >
            <option value="10">1-10</option>
            <option value="20">1-20</option>
            <option value="30">1-30</option>
          </select>
          <button
            disabled={isGetPatientLoading ? true : false}
            className={styles.mobilePaginationNextButton}
            onClick={() => {
              if (
                parseInt(paginationState.page) + 1 <=
                Math.ceil(patients?.total_record / paginationState.limit)
              ) {
                setPaginationState((previous) => {
                  return {
                    ...previous,
                    page: parseInt(previous.page) + 1,
                  }
                })
              }
            }}
          >
            Next
            <ArrowRightIcon stroke="var(--pure-white)" />
          </button>
        </div>

        <Pagination
          disabled={isGetPatientLoading ? true : false}
          count={Math.ceil(patients?.total_record / paginationState.limit)}
          siblingCount={1}
          boundaryCount={1}
          page={paginationState.page}
          onChange={(event, page) => {
            setPaginationState((previous) => {
              return { ...previous, page }
            })
          }}
          sx={{
            "@media (max-width:700px)": {
              display: "none",
            },
          }}
          renderItem={(item) => (
            <PaginationItem
              components={{
                previous: () => (
                  <div className={styles.paginationPreviousButton}>
                    <ArrowLeftIcon />
                    Previous
                  </div>
                ),
                next: () => (
                  <div className={styles.paginationNextButton}>
                    Next
                    <ArrowRightIcon />
                  </div>
                ),
              }}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "var(--primary-blue-light)",
                },
              }}
              {...item}
            />
          )}
          shape="rounded"
        />
      </TableFooter>
    </div>
  )
}

export default PatientDetailsTable
